package com.exura.features.plandefinition.domain.model.plan

import com.exura.features.firebase.domain.model.SerializableDomainModel
import com.exura.features.plandefinition.domain.model.PlanContentSummary
import com.exura.features.plandefinition.domain.model.collection.Collection
import com.exura.features.plandefinition.domain.model.element.BasePlanElement
import com.exura.features.plandefinition.domain.model.series.BaseAnchoredSeries
import com.exura.features.plandefinition.domain.model.series.BaseSeries
import com.exura.features.plandefinition.domain.model.series.SeriesSpawnType
import dev.gitlive.firebase.firestore.CollectionReference
import kotlin.collections.set
import kotlin.math.max
import kotlin.math.min

/**
 * Represents a medical plan that can be assigned to a patient.
 * Implements SerializableDomainModel for Firebase storage.
 */
abstract class BasePlan(
    override val firebaseDocId: String? = null,
    override val firebaseDocPrefix: CollectionReference? = null,
    val planType: PlanType,
) : SerializableDomainModel {
    abstract val title: String
    abstract val description: String
    abstract val imageUrl: String?
    abstract val collections: List<Collection>
    abstract val directSeries: List<BaseSeries<out BasePlanElement>>

    fun summary() : PlanContentSummary {
        val counts = countSeriesByType()
        return PlanContentSummary(
            title = title,
            description = description,
            imageUrl = imageUrl,
            planDuration = durationInDays(),
            numberInstruction = counts[SeriesSpawnType.INSTRUCTION] ?: 0,
            numberPersistent = counts[SeriesSpawnType.PERSISTENT] ?: 0,
            numberQuestion = counts[SeriesSpawnType.QUESTION] ?: 0,
            numberFloating = counts[SeriesSpawnType.FLOATING] ?: 0,
        )
    }

    fun countSeriesByType(): Map<SeriesSpawnType, Int> {
        val counts = mutableMapOf<SeriesSpawnType, Int>().apply {
            // Initialize all types to 0
            SeriesSpawnType.values().forEach { seriesType ->
                this[seriesType] = 0
            }
        }

        // Count direct series
        directSeries.forEach { series ->
            val type = series.seriesSpawnType
            counts[type] = (counts[type] ?: 0) + 1
        }

        // Count series within collections
        collections.forEach { collection ->
            collection.series.forEach { series ->
                val type = series.seriesSpawnType
                counts[type] = (counts[type] ?: 0) + 1
            }
        }

        return counts
    }

    // Existing methods remain the same
    fun durationInDays(): Int {
        if (directSeries.isEmpty() && collections.isEmpty()) return 0

        // Find earliest start day, accounting for collection.relativeDay
        val minDirectSeriesDay = directSeries
            .mapNotNull { it as? BaseAnchoredSeries<*> }.minOfOrNull { it.relativeDay } ?: Int.MAX_VALUE

        val minCollectionDay = collections
            .flatMap { collection ->
                collection.series
                    .mapNotNull { it as? BaseAnchoredSeries<*> }
                    .map { collection.relativeDay + it.relativeDay }
            }
            .minOrNull() ?: Int.MAX_VALUE

        val earliestDay = min(minDirectSeriesDay, minCollectionDay)

        // Find latest end day, accounting for collection.relativeDay
        val maxDirectSeriesEndDay = directSeries
            .mapNotNull { series ->
                val startDay = (series as? BaseAnchoredSeries<*>)?.relativeDay
                val duration = series.durationInDays()
                if (startDay != null && duration != null) {
                    startDay + duration - 1
                } else null
            }
            .maxOrNull() ?: Int.MIN_VALUE

        val maxCollectionEndDay = collections
            .flatMap { collection ->
                collection.series.mapNotNull { series ->
                    val startDay = (series as? BaseAnchoredSeries<*>)?.relativeDay
                    val duration = series.durationInDays()
                    if (startDay != null && duration != null) {
                        collection.relativeDay + startDay + duration - 1
                    } else null
                }
            }
            .maxOrNull() ?: Int.MIN_VALUE

        val latestDay = max(maxDirectSeriesEndDay, maxCollectionEndDay)

        return if (earliestDay != Int.MAX_VALUE && latestDay != Int.MIN_VALUE) {
            latestDay - earliestDay + 1
        } else {
            0
        }
    }
}
